import { assign } from 'xstate'
import dayjs from 'dayjs'
import { ReviewerRole, ReviewPrivacy, ReviewType } from './types'
import { toChannelValue, toUserValue } from './util'
import { ReviewTemplate } from 'types'
import { reviewRolesFromReviewTypeAndConfig, ReviewTypeConfig } from './reviewConfig'
import { BackEvent, Context, EventId } from './machineTypes'
import { reviewTitle } from './reviewTitle'
import { ChannelValue, UserValue } from 'common/RecipientBadge/types'
import { reviewTypeFromTemplate } from 'components/ReviewTemplateFormModal/ReviewTemplateForm/utils'

const membersFromTemplate = (
  type: 'reviewers' | 'approvers' | 'viewers',
  template: ReviewTemplate,
): Array<ChannelValue | UserValue> => {
  const users = template[`user_${type}`].map(toUserValue)
  const channels = template[`channel_${type}`].map(toChannelValue)
  return [...users, ...channels]
}

export function assignTemplate<T extends { reviewTemplate: ReviewTemplate; type: EventId }>(canChangePrivacy: boolean) {
  return assign<Context, T>({
    reviewTemplate: (_, { reviewTemplate }) => reviewTemplate,
    title: (_, { reviewTemplate }) => reviewTemplate.name,
    message: (_, { reviewTemplate }) => reviewTemplate.description,
    htmlMessage: (_, { reviewTemplate }) => reviewTemplate.html_description || undefined,
    endDate: (_, { reviewTemplate }) => dayjs().add(reviewTemplate.days_ahead, 'day').format('YYYY-MM-DD'),
    reviewType: (_, { reviewTemplate }) => reviewTypeFromTemplate(reviewTemplate),
    reviewPrivacy: (_, { reviewTemplate }) => (canChangePrivacy ? reviewTemplate.privacy : ReviewPrivacy.PUBLIC),
    reviewers: (_, { reviewTemplate }) => membersFromTemplate('reviewers', reviewTemplate),
    approvers: (_, { reviewTemplate }) => membersFromTemplate('approvers', reviewTemplate),
    viewers: (_, { reviewTemplate }) => membersFromTemplate('viewers', reviewTemplate),
    reviewRoles: (ctx, { reviewTemplate }) =>
      reviewRolesFromReviewTypeAndConfig(ctx.reviewType || ReviewType.FEEDBACK, asReviewConfig(reviewTemplate)),
  })
}

export function clearReviewTemplateAssigns(defaultTitle: string, defaultEndDate: string) {
  return assign<Context, BackEvent>({
    reviewTemplate: (_, __) => null,
    title: (context, __) =>
      reviewTitle(context.asset?.title || defaultTitle, context.reviewType || ReviewType.FEEDBACK),
    message: (_, __) => '',
    htmlMessage: (_, __) => '',
    endDate: (_, __) => defaultEndDate,
    reviewType: (context, __) => context.reviewType || ReviewType.FEEDBACK,
    viewers: (_, __) => [],
    reviewers: (_, __) => [],
    approvers: (_, __) => [],
    reviewRoles: (_, __) => [ReviewerRole.REVIEWER],
  })
}

function asReviewConfig(template: ReviewTemplate): ReviewTypeConfig {
  return {
    reviewers: membersFromTemplate('reviewers', template),
    approvers: membersFromTemplate('approvers', template),
    viewers: membersFromTemplate('viewers', template),
  }
}
